import { makeStyles } from 'tss-react/mui';

/**
 * Styles for the markdown rendering surface inside an assistant
 * message bubble.
 *
 * Descendant selectors on the root keep `react-markdown` component
 * overrides limited to element substitution and link behavior.
 */
const useStyles = makeStyles()((theme) => {
  const sharedBody = {
    ...theme.typography.small_body_regular,
    lineHeight: 1.6,
  };
  const sharedBodyMedium = {
    ...theme.typography.small_body_medium,
    lineHeight: 1.6,
  };

  return {
    root: {
      width: '100%',
      color: theme.new.color.textPrimary,
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      '& > *:first-of-type': {
        marginTop: 0,
      },
      '& > *:last-child': {
        marginBottom: 0,
      },
      '& p': {
        ...sharedBody,
        margin: `${theme.new.spacing.small} 0`,
      },
      '& h1': {
        ...theme.typography.header1_medium,
        margin: `${theme.new.spacing.medium} 0 ${theme.new.spacing.small}`,
      },
      '& h2': {
        ...theme.typography.header2_medium,
        margin: `${theme.new.spacing.medium} 0 ${theme.new.spacing.small}`,
      },
      '& h3, & h4, & h5, & h6': {
        ...sharedBodyMedium,
        margin: `${theme.new.spacing.small} 0`,
      },
      '& strong, & b': {
        ...sharedBodyMedium,
      },
      '& em, & i': {
        fontStyle: 'italic',
      },
      '& a': {
        color: theme.new.color.brand[600],
        textDecoration: 'underline',
        '&:hover': {
          textDecoration: 'none',
        },
      },
      '& ul, & ol': {
        paddingInlineStart: 24,
        margin: `${theme.new.spacing.small} 0`,
      },
      '& li': {
        ...sharedBody,
        margin: `${theme.new.spacing.xsmall} 0`,
      },
      '& li.task-list-item': {
        listStyle: 'none',
        marginInlineStart: -24,
      },
      '& li.task-list-item input[type="checkbox"]': {
        marginRight: theme.new.spacing.small,
      },
      '& blockquote': {
        borderLeft: `3px solid ${theme.new.color.grey[400]}`,
        padding: `${theme.new.spacing.xsmall} ${theme.new.spacing.medium}`,
        margin: `${theme.new.spacing.small} 0`,
        color: theme.new.color.textSecondary,
        '& > p': {
          margin: 0,
        },
      },
      '& code': {
        ...theme.typography.small_body_regular,
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        backgroundColor: theme.new.color.grey[200],
        padding: `2px ${theme.new.spacing.xsmall}`,
        borderRadius: theme.new.shape.borderRadius.small,
      },
      '& pre': {
        margin: `${theme.new.spacing.small} 0`,
        padding: theme.new.spacing.small,
        backgroundColor: theme.new.color.grey[200],
        borderRadius: theme.new.shape.borderRadius.medium,
        overflowX: 'auto',
        maxHeight: 320,
      },
      '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
        borderRadius: 0,
      },
      '& hr': {
        border: 0,
        height: 1,
        backgroundColor: theme.new.color.grey[400],
        margin: `${theme.new.spacing.medium} 0`,
      },
      '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        margin: `${theme.new.spacing.small} 0`,
        ...theme.typography.small_body_regular,
      },
      '& th, & td': {
        border: `1px solid ${theme.new.color.grey[400]}`,
        padding: `${theme.new.spacing.xsmall} ${theme.new.spacing.small}`,
        textAlign: 'left',
      },
      '& th': {
        backgroundColor: theme.new.color.grey[200],
        ...theme.typography.small_body_medium,
      },
      '& del, & s': {
        textDecoration: 'line-through',
      },
    },
  };
});

export default useStyles;
